export type RetryOptions = {
  maxRetries?: number;
  delayMs?: number;
  shouldRetry?: (error: unknown, attempt: number) => boolean;
  getDelayMs?: (error: unknown, attempt: number) => number;
};

export async function retry<T>(fn: () => Promise<T>, options?: RetryOptions): Promise<T>;
export async function retry<T>(fn: () => Promise<T>, maxRetries?: number, delayMs?: number): Promise<T>;
export async function retry<T>(
  fn: () => Promise<T>,
  maxRetriesOrOptions: number | RetryOptions = 10,
  delayMs: number = 5000,
): Promise<T> {
  const options: RetryOptions =
    typeof maxRetriesOrOptions === 'object'
      ? maxRetriesOrOptions
      : { maxRetries: maxRetriesOrOptions, delayMs };

  const resolvedMaxRetries = options.maxRetries ?? 10;
  const resolvedDelayMs = options.delayMs ?? 5000;
  const shouldRetry = options.shouldRetry ?? (() => true);
  const getDelayMs = options.getDelayMs ?? (() => resolvedDelayMs);

  let lastError: unknown;

  for (let attempt = 1; attempt <= resolvedMaxRetries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;

      const canRetry = attempt < resolvedMaxRetries && shouldRetry(err, attempt);
      if (!canRetry) break;

      const waitMs = getDelayMs(err, attempt);
      if (waitMs > 0) {
        await new Promise<void>((resolve) => setTimeout(resolve, waitMs));
      }
    }
  }

  throw lastError;
}
